// ?===============================
// ?=====  3rd party Imports  =====
// ?===============================

import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'gatsby-link'
import { Flex, NavLink, Box } from '@theme-ui/components'
import { nanoid } from 'nanoid'

// ?===================================
// ?===  Local Components Imports  ====
// ?===================================

import CloudinaryImage from '../../../../General/CloudinaryImage'
import SocialIcons from '../../../../General/SocialIcons/SocialIconsV1'
import LocationSwitcher from '../../../../LocationSwitchers/v1/LocationSwitcher'

// ?===================================
// ?====  Local Function Imports  =====
// ?===================================
import multiLocationLink from '../../../../../helpers/multiLocationLink'
import combineSocialLinks from '../../../../../helpers/combineSocialLinks'
import checkIfExternalLink from '../../../../../helpers/checkIfExternalLink'

// ?===================================
// ?==========  Component  ============
// ?===================================

export default function Navigation(props) {
  // ?==========================
  // ?====== Destructing  ======
  // ?==========================
  const {
    businessData,
    configData,
    // customPageName,
    // gonationID
    isMultiLocationSite,
    links,
    locationIndex,
    locations
    // navLinks,
    // pageComponents,
    // pageObjectKey
  } = props

  const {
    avatar: { imageBaseUrl, imagePrefix },
    links: socialLinks,
    slug,
    city
  } = businessData

  const { variantName, options } = configData.navigation

  // * ===== Component Options =====
  const { containerStyles, socialLinksHidden, navigationLogo, linkedin, pinterest } = options

  // ?===========================
  // ?===== Component State =====
  // ?===========================
  const [navOpen, toggleNav] = useState(false)
  const [navBackground, setNavBackground] = useState(false)

  // ?==============================
  // ?=====  Variable Creation  ====
  // ?==============================
  const variant = variantName ? variantName : 'navigationV1'
  const styles = containerStyles ? JSON.parse(containerStyles) : {}
  const navRef = useRef()
  navRef.current = navBackground
  const configuredSocialLinks =
    linkedin || pinterest
      ? combineSocialLinks(socialLinks, linkedin?.[locationIndex], pinterest?.[locationIndex])
      : socialLinks

  // ?=============================================
  // ?==============   Functions   ================
  // ?=============================================

  // ?============================================
  // ?===========   ON/OFF MOUNTING   ============
  // ?============================================

  useEffect(() => {
    const handleScroll = () => {
      const show = window.scrollY > 70
      if (navRef.current !== show) {
        setNavBackground(show)
      }
    }
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // ?=========================
  // ?===== JSX rendering =====
  // ?=========================

  return (
    <Flex variant={`${variant}.container`} as="nav" sx={styles} className="container navContainer">
      {navigationLogo ? (
        <Flex
          variant={`${variant}.${navBackground ? 'logoScrolled' : 'logo'}`}
          className={`${navBackground ? 'logoScrolled' : 'logo'}`}
        >
          <Link to="/">
            <CloudinaryImage imageBaseUrl={imageBaseUrl} imagePrefix={imagePrefix} />
          </Link>
        </Flex>
      ) : (
        ''
      )}

      {isMultiLocationSite && (
        <Flex variant={`${variant}.locationSwitcher`} className="locationSwitcher">
          <LocationSwitcher businessData={businessData} locations={locations} />
        </Flex>
      )}

      <Flex
        variant={`${!navOpen ? `${variant}.hamburger` : `${variant}.hamburgerOpen`}`}
        onClick={() => {
          toggleNav(!navOpen)
        }}
        className={navOpen ? 'navopen' : ''}
      >
        <div className="top"></div>
        {!navOpen ? <div className="middle"></div> : ''}
        <div className="bottom"></div>
      </Flex>

      <Flex variant={`${variant}.navMenu`} style={{ left: `${navOpen ? '0%' : '-150%'}` }} className="navMenu">
        <Box variant={`${variant}.menuLogo`} className={`menuLogo menuLogoContainer`}>
          <Link to="/">
            <CloudinaryImage imageBaseUrl={imageBaseUrl} imagePrefix={imagePrefix} />
          </Link>
        </Box>
        {links.map(page => {
          const { ctaName, ctaLink } = page
          return (
            <NavLink as="li" variant={`${variant}.navItem`} className="navItem" key={nanoid()}>
              {checkIfExternalLink(page.ctaLink) ? (
                <a href={ctaLink} target="_blank" rel="noreferrer">
                  {ctaName}
                </a>
              ) : (
                <Link to={isMultiLocationSite ? multiLocationLink(city, ctaLink) : `/${ctaLink}`}>{ctaName}</Link>
              )}
            </NavLink>
          )
        })}
        {socialLinksHidden ? (
          ''
        ) : (
          <Flex as="li" variant={`${variant}.socialIconsContainer`} className="navItem">
            <SocialIcons links={configuredSocialLinks} slug={slug} />
          </Flex>
        )}
      </Flex>
      <Box
        style={{ left: `${navOpen ? '0%' : '-500%'}` }}
        variant={`${variant}.navBlock`}
        className="navBlock"
        onClick={() => {
          toggleNav(!navOpen)
        }}
      />
    </Flex>
  )
}
